import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  ViewChildren,
  ViewChild,
  ElementRef,
  QueryList,
  ChangeDetectorRef,
  inject,
  input,
  output,
  AfterViewInit,
} from '@angular/core';
import { register } from 'swiper/element/bundle';
import { Swiper } from 'swiper/types';
import { ImageViewerComponent } from '../../../../../../shared/components/image-viewer/image-viewer.component';
import { PdfViewerComponent } from '../../../../../../shared/components/pdf-viewer/pdf-viewer.component';
import { retrieveFileTypeFromS3Url } from '../../_helpers/transactions-helpers';
import { TransactionReceiptFile } from '../../../../../../_interfaces/transactions.interface';
import { calcConditionalEnvBasedUrl } from '../../_helpers/localstack-url-helper';

// Register Swiper custom elements
register();

@Component({
  selector: 'app-receipt-carousel',
  imports: [ImageViewerComponent, PdfViewerComponent],
  templateUrl: './receipt-carousel.html',
  styleUrls: [
    '../../../../../../../static/styles/components/receipt-preview.component.scss',
    './receipt-carousel.scss',
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class ReceiptCarousel implements AfterViewInit {
  @ViewChildren(PdfViewerComponent) pdfViewers!: QueryList<PdfViewerComponent>;
  @ViewChild('swiperContainer', { read: ElementRef }) swiperContainer?: ElementRef;

  readonly cdr = inject(ChangeDetectorRef);
  receipts = input<TransactionReceiptFile[]>([] as TransactionReceiptFile[]);
  initialSlide = input<number>(0); // Initial slide to show when carousel opens
  closePreview = output<boolean>();
  swiperReady = output<Swiper>();
  slideChanged = output<number>(); // Emit the active slide index

  currentSlideIndex = 0;
  loadedSlides = new Set<number>([0]); // Load first slide by default
  swiperInstance?: Swiper;

  ngAfterViewInit() {
    // Get the swiper instance after view init
    // Swiper custom elements need a small delay to fully initialize
    setTimeout(() => {
      if (this.swiperContainer) {
        const swiperEl = this.swiperContainer.nativeElement;
        this.swiperInstance = swiperEl.swiper;

        // Emit the swiper instance to parent
        if (this.swiperInstance) {
          // Update current slide index to match the initial slide
          this.currentSlideIndex = this.initialSlide();
          this.loadedSlides.add(this.currentSlideIndex);

          this.swiperReady.emit(this.swiperInstance);
        }
      }
    }, 100);
  }

  /**
   * Navigate to a specific slide (can be called from parent)
   */
  slideTo(index: number): void {
    if (this.swiperInstance) {
      this.swiperInstance.slideTo(index);
    }
  }

  onProgress(event: CustomEvent) {
    if (event.detail && Array.isArray(event.detail)) {
      const [progress] = event.detail;
      // Only log if progress is a valid number
      if (typeof progress === 'number' && !Number.isNaN(progress)) {
        console.log('Progress:', progress);
      }
    }
  }

  onSlideChange(event: CustomEvent) {
    if (event.detail && Array.isArray(event.detail)) {
      const [swiper] = event.detail;
      const newIndex = swiper.activeIndex;
      this.currentSlideIndex = newIndex;

      // Emit the slide change to parent for thumbnail active state
      this.slideChanged.emit(newIndex);

      // Mark the current slide as loaded
      this.loadedSlides.add(this.currentSlideIndex);

      // Check if the new slide is a PDF by parsing the URL
      const currentReceipt = this.receipts()[newIndex];
      const fileType = currentReceipt ? retrieveFileTypeFromS3Url(currentReceipt.url) : null;

      if (fileType === 'pdf') {
        // Trigger change detection and resize after a delay
        setTimeout(() => {
          this.cdr.detectChanges();
          this.triggerPdfResize();

          // Try again after a longer delay
          setTimeout(() => {
            this.triggerPdfResize();
          }, 300);
        }, 100);
      }
    }
  }

  isSlideLoaded(index: number): boolean {
    return this.loadedSlides.has(index);
  }

  getFileType(url: string): string | null {
    return retrieveFileTypeFromS3Url(url);
  }

  private triggerPdfResize(): void {
    // Trigger a window resize event to force PDF viewer to recalculate dimensions
    window.dispatchEvent(new Event('resize'));
  }

  protected readonly calcConditionalEnvBasedUrl = calcConditionalEnvBasedUrl;

  onClose(): void {
    this.closePreview.emit(true);
  }
}
